import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";

import { pb, logout } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { api } from "../lib/api";
import { inputValue, errorMessage } from "../lib/dom";
import { sharedStyles, authStyles } from "../styles/shared";
import type { Membership, Organization } from "../lib/types";

@customElement("onboarding-page")
export class OnboardingPage extends LitElement {
  static styles = [
    sharedStyles,
    authStyles,
    css`
      .auth-card {
        max-width: 480px;
      }
      .mode-tabs {
        display: flex;
        gap: 8px;
        margin-bottom: 8px;
      }
      .mode-tabs button {
        flex: 1;
        border: 1px solid var(--md-sys-color-outline-variant);
        background: none;
        border-radius: 999px;
        padding: 8px 12px;
        font: inherit;
        font-weight: 600;
        font-size: 0.8125rem;
        cursor: pointer;
        color: var(--md-sys-color-on-surface-variant);
      }
      .mode-tabs button.active {
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
        border-color: transparent;
      }
    `,
  ];

  @state() private mode: "create" | "join" = "create";
  @state() private orgName = "";
  @state() private token = "";
  @state() private loading = false;
  @state() private error = "";

  connectedCallback(): void {
    super.connectedCallback();
    // The URL is the only source: a signed-out invitee's link survives the
    // login bounce because the auth guard stashes the FULL intended URL and
    // login/signup restore it with a real load (see router.ts consumeReturnTo),
    // so the query parameter is always here by the time this page mounts.
    const invite = new URLSearchParams(window.location.search).get("invite");
    if (invite) {
      this.mode = "join";
      this.token = invite;
    }
  }

  private async handleCreate(e: Event): Promise<void> {
    e.preventDefault();
    this.error = "";
    this.loading = true;
    try {
      await api.post<Organization>("/tenancy/organizations", {
        name: this.orgName,
      });
      await this.finishOnboarding();
    } catch (err) {
      this.error = errorMessage(err, "Could not create the organization.");
    } finally {
      this.loading = false;
    }
  }

  private async handleJoin(e: Event): Promise<void> {
    e.preventDefault();
    this.error = "";
    this.loading = true;
    try {
      await api.post<Membership>("/tenancy/invitations/accept", {
        token: this.token,
      });
      await this.finishOnboarding();
    } catch (err) {
      this.error = errorMessage(
        err,
        "This invitation link is invalid or has expired.",
      );
    } finally {
      this.loading = false;
    }
  }

  private async finishOnboarding(): Promise<void> {
    // The server-side roles/organization update only takes effect on the auth
    // record after a refresh — pull the updated record before routing on.
    await pb.collection("tenancy_users").authRefresh();
    await navigate("/");
  }

  private handleLogout = (): void => {
    logout();
    void navigate("/login");
  };

  render() {
    return html`
      <div class="auth-page">
        <div class="auth-card">
          <h1>Welcome aboard</h1>
          <p class="subtitle">
            Create a new organization, or join one you've been invited to.
          </p>

          <div class="mode-tabs" role="tablist">
            <button
              role="tab"
              class=${this.mode === "create" ? "active" : ""}
              @click=${() => (this.mode = "create")}
            >
              Create organization
            </button>
            <button
              role="tab"
              class=${this.mode === "join" ? "active" : ""}
              @click=${() => (this.mode = "join")}
            >
              Join with invite
            </button>
          </div>

          ${this.mode === "create"
            ? html`
                <form @submit=${(e: Event) => void this.handleCreate(e)}>
                  <md-outlined-text-field
                    label="Organization name"
                    required
                    .value=${this.orgName}
                    @input=${(e: Event) => (this.orgName = inputValue(e))}
                  ></md-outlined-text-field>
                  ${this.error
                    ? html`<p class="error-text">${this.error}</p>`
                    : null}
                  <md-filled-button type="submit" ?disabled=${this.loading}>
                    ${this.loading ? "Creating…" : "Create organization"}
                  </md-filled-button>
                </form>
              `
            : html`
                <form @submit=${(e: Event) => void this.handleJoin(e)}>
                  <md-outlined-text-field
                    label="Invitation token"
                    required
                    .value=${this.token}
                    @input=${(e: Event) => (this.token = inputValue(e))}
                  ></md-outlined-text-field>
                  ${this.error
                    ? html`<p class="error-text">${this.error}</p>`
                    : null}
                  <md-filled-button type="submit" ?disabled=${this.loading}>
                    ${this.loading ? "Joining…" : "Join organization"}
                  </md-filled-button>
                </form>
              `}

          <p class="footer-link">
            <md-text-button @click=${this.handleLogout}
              >Sign out</md-text-button
            >
          </p>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "onboarding-page": OnboardingPage;
  }
}
